import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Divider } from "primereact/divider";
import { FileUpload } from "primereact/fileupload";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { Toast } from "primereact/toast";
import { classNames } from "primereact/utils";
import { useContext, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { DEFAULT_COUNTRY_CODE, DEFAULT_STATE_CODE, getErrorTitle, WEBSITE_TEMPLATE_MAX_IMAGE_FILE_SIZE_IN_BYTES } from "../../../constants/constants";
import { ROLE_TYPE_CAREGIVER, ROLE_TYPE_MHP, ROLE_TYPE_NURSE, ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { RouterAddress } from "../../../constants/RouterAddress";
import { UserProfile } from "../../../models/userProfile";
import { extractLocationDetails } from "../../../utils/stringUtils";
import { normalizePlainText } from "../../../utils/securityUtils";
import SearchAddress from "../../../components/general/SearchAddress";
import Unauthorized from "../../../shared/pages/Unauthorized";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";

const ALL_ADDITIONAL_ROLE_OPTIONS = [
  { value: ROLE_TYPE_PROVIDER, label: "Provider" },
  { value: ROLE_TYPE_CAREGIVER, label: "Caregiver" },
  { value: ROLE_TYPE_NURSE, label: "Registered Nurse" },
  { value: ROLE_TYPE_MHP, label: "Mental Health Professional" },
];

const Profile = () => {
  const toast = useRef<Toast>(null);
  const fileUploadRef = useRef<FileUpload>(null);
  const navigate = useNavigate();
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const authExperience = useAuthExperience();
  const [profileErrorMessage, setProfileErrorMessage] = useState<string | null>(null);
  const [editProfileErrorMessage, setEditProfileErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [editProfileLoading, setEditProfileLoading] = useState<boolean>(false);
  const [profileImageUrl, setProfileImageUrl] = useState<string>();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [showEditProfileModal, setShowEditProfileModal] = useState<boolean>(false);
  const [userId, setUserId] = useState<string>(authStore.getUserId());
  const [communicationPreferences, setCommunicationPreferences] = useState<any | null>(null);
  const [loadingCommunicationPreferences, setLoadingCommunicationPreferences] = useState<boolean>(false);
  const [savingCommunicationPreferences, setSavingCommunicationPreferences] = useState<boolean>(false);

  const userRoleType = authStore.getUserRoleType();
  const canEditAdditionalRoles = userRoleType === ROLE_TYPE_PROVIDER;
  const userEmail = authStore.getUserEmailId();
  let userName = authStore.getUserName();

  const [addressLine1, setAddressLine1] = useState<string>("");
  const [addressLine2, setAddressLine2] = useState<string>("");
  const [formattedAddress, setFormattedAddress] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [state, setState] = useState<string>("");
  const [country, setCountry] = useState<string>("");
  const [postalCode, setPostalCode] = useState<string>("");
  const [latitude, setLatitude] = useState<string>("");
  const [longitude, setLongitude] = useState<string>("");
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [providerOneId, setProviderOneId] = useState<string>("");
  const [displayInitials, setDisplayInitials] = useState<string>("");
  const [selectedRoleTypes, setSelectedRoleTypes] = useState<string[]>([]);

  const deriveInitials = (sourceFirstName: string, sourceLastName: string) =>
    [String(sourceFirstName || "").trim(), String(sourceLastName || "").trim()]
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("");

  const defaultValues = {
    firstName: '',
    lastName: '',
    phoneNumber: '',
    providerOneId: '',
    displayInitials: '',
    address: '',
    email: '',
    userName: '',
    additionalRoleTypes: [],
  }

  const { control, formState: { errors }, handleSubmit, reset, setValue } = useForm({ defaultValues });

  const applyProfileToView = (userDetails: any) => {
    const normalizedStoredRoleTypes = Array.isArray(userDetails.selectedRoleTypes)
      ? userDetails.selectedRoleTypes.map((role: string) => String(role || "").trim().toLowerCase()).filter(Boolean)
      : [];
    const extraRoleTypes = normalizedStoredRoleTypes.filter((role: string) => role !== userRoleType);
    reset({
      email: userDetails.email || '',
      userName: userDetails.userName || '',
      firstName: userDetails.firstName || '',
      lastName: userDetails.lastName || '',
      phoneNumber: userDetails.phoneNumber || '',
      providerOneId: userDetails.providerOneId || '',
      displayInitials: userDetails.displayInitials || '',
      address: userDetails.formattedAddress || '',
      additionalRoleTypes: extraRoleTypes,
    });
    setFirstName(userDetails.firstName || '');
    setLastName(userDetails.lastName || '');
    setPhoneNumber(userDetails.phoneNumber || '');
    setProviderOneId(userDetails.providerOneId || '');
    setDisplayInitials(userDetails.displayInitials || '');
    setFormattedAddress(userDetails.formattedAddress || '');
    setAddressLine1(userDetails.addressLine1 || '');
    setAddressLine2(userDetails.addressLine2 || '');
    setCity(userDetails.city || '');
    setState(userDetails.state || '');
    setCountry(userDetails.country || '');
    setPostalCode(userDetails.zipcode || '');
    setLatitude(userDetails.latitude || '');
    setLongitude(userDetails.longitude || '');
    setSelectedRoleTypes(normalizedStoredRoleTypes.length ? normalizedStoredRoleTypes : (userRoleType ? [userRoleType] : []));
  };

  const getFormErrorMessage = (name) => {
    return errors[name] && <small className="p-error">{errors[name].message}</small>
  };

  useEffect(() => {
    const fetchProfileData = async () => {
      setLoading(true);
      try {
        await backendAPIStore.init();
        const authenticated = await authStore.isUserLoggedIn();
        if (authenticated) {
          setUserId(authStore.getUserId());
          setLoadingCommunicationPreferences(true);
          const [userDetailsResult, preferenceDetailsResult] = await Promise.allSettled([
            backendAPIStore.getUserProfile(authStore.getUserId()),
            backendAPIStore.getCurrentUserPreferences(),
          ]);
          const userDetails = userDetailsResult.status === "fulfilled" ? userDetailsResult.value : null;
          if (!userDetails) {
            setProfileErrorMessage("Please complete your profile information.");
            return;
          }
          await authStore.loadUserContext(true).catch(() => null);
          const profileImageUrl = await backendAPIStore.getPresignedUrlForProfilePicDownload(authStore.getUserId());
          setProfileImageUrl(profileImageUrl || '');
          applyProfileToView(userDetails);
          if (preferenceDetailsResult.status === "fulfilled") {
            setCommunicationPreferences(preferenceDetailsResult.value || null);
          } else {
            setCommunicationPreferences(null);
          }
        } else {
          navigate(RouterAddress.LOGIN_PAGE);
          return;
        }


      } catch (error: any) {
        setProfileErrorMessage(error.message);
      } finally {
        setLoading(false);
        setLoadingCommunicationPreferences(false);
      }
    };

    fetchProfileData();
  }, [userId]);

  const handleAddressChange = (e) => {
    const place = e.target?.place;
    if (place) {
      const details = extractLocationDetails(place);
      setAddressLine1(details.addressLine1 || "");
      setAddressLine2(details.addressLine2 || "");
      setFormattedAddress(place.formatted_address || "");
      setCity(details.city || "");
      setState(details.state || "");
      setCountry(details.country || "");
      setPostalCode(details.postalCode || "");
      setLatitude(place.geometry?.location?.lat() || "");
      setLongitude(place.geometry?.location?.lng() || "");
      setEditProfileErrorMessage("");
      setValue("address", place.formatted_address || '', { shouldValidate: true });
    } else {
      setValue("address", '', { shouldValidate: true });
    }
  };

  const onSubmit = async (formData: any) => {
    if (formattedAddress === "") {
      setEditProfileErrorMessage("Please enter a valid complete address from the input suggestions.");
      return;
    }

    if (!addressLine1 || !city || !state || !country || !postalCode) {
      setEditProfileErrorMessage("The selected address is missing some required components (street address, city, state, country, or postal code). Please select a different address or modify the current address to include these components.");
      return;
    }

    if (state !== DEFAULT_STATE_CODE && country === DEFAULT_COUNTRY_CODE) {
      setEditProfileErrorMessage(`Sorry, currently we only support addresses in ${DEFAULT_STATE_CODE} state`);
      return;
    }

    setEditProfileErrorMessage(null);
    setEditProfileLoading(true);

    let tempData = new UserProfile();
    tempData.firstName = normalizePlainText(formData.firstName, { maxLength: 100 });
    tempData.lastName = normalizePlainText(formData.lastName, { maxLength: 100 });
    tempData.phoneNumber = normalizePlainText(formData.phoneNumber, { maxLength: 15 });
    tempData.providerOneId = normalizePlainText(formData.providerOneId, { maxLength: 40 });
    tempData.displayInitials = normalizePlainText(formData.displayInitials, { maxLength: 6 }).toUpperCase();
    tempData.addressLine1 = addressLine1;
    tempData.addressLine2 = addressLine2;
    tempData.formattedAddress = formattedAddress;
    tempData.city = city;
    tempData.state = state;
    tempData.country = country;
    tempData.zipcode = postalCode;
    tempData.latitude = latitude;
    tempData.longitude = longitude;

    tempData.userId = userId;
    tempData.email = normalizePlainText(userEmail, { maxLength: 160 }).toLowerCase();
    tempData.userRoleType = userRoleType;
    tempData.selectedRoleTypes = Array.from(new Set([
      String(userRoleType || "").trim().toLowerCase(),
      ...(Array.isArray(formData.additionalRoleTypes) ? formData.additionalRoleTypes.map((role: string) => String(role || "").trim().toLowerCase()) : []),
    ].filter(Boolean)));

    tempData.active = formData.active || true;
    tempData.createdAt = formData.createdAt;
    tempData.createdBy = formData.createdBy;

    try {
      const response = await backendAPIStore.updateUserProfile(tempData);
      if (response && selectedFile) {
        await uploadProfileImage(userId);
      }
      toast.current?.show({ severity: 'success', summary: 'Success', detail: 'Profile updated successfully.', life: 3000 });
      window.setTimeout(() => {
        window.location.reload();
      }, 250);
    } catch (error: any) {
      setEditProfileErrorMessage(error.message);
      return;
    } finally {
      setEditProfileLoading(false);
      setShowEditProfileModal(false);
    }
  };

  const uploadProfileImage = async (userId: string) => {
    if (!selectedFile) return null;
    try {
      const presignedUrl = await backendAPIStore.getPresignedUrlForProfilePicUpload(userId);
      await backendAPIStore.uploadFileToS3(presignedUrl, selectedFile);
      return presignedUrl.split("?")[0];
    } catch (error: any) {
      toast.current?.show({ severity: 'error', summary: getErrorTitle(error), detail: error['message'], life: 3000 });
      setEditProfileErrorMessage("Failed to upload image.");
      return null;
    }
  };

  const resetAddressFields = () => {
    setAddressLine1("");
    setAddressLine2("");
    setFormattedAddress("");
    setCity("");
    setState("");
    setCountry("");
    setPostalCode("");
    setLatitude("");
    setLongitude("");
    setEditProfileErrorMessage("");
  }

  const onCommunicationPreferenceChange = async (nextSubscribed: boolean) => {
    setSavingCommunicationPreferences(true);
    try {
      const response = await backendAPIStore.updateCurrentUserPreferences({
        emailCampaignSubscribed: nextSubscribed,
      });
      setCommunicationPreferences(response || null);
      toast.current?.show({
        severity: "success",
        summary: nextSubscribed ? "Subscribed" : "Unsubscribed",
        detail: nextSubscribed
          ? "You will receive admin product update emails again."
          : "You will no longer receive admin product update emails.",
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error.message, life: 3000 });
    } finally {
      setSavingCommunicationPreferences(false);
    }
  };

  const titleTemplate = (title) => {
    return (
      <div className="flex flex-wrap align-items-center justify-content-between gap-2">
        <span className="text-xl text-900">{title}</span>
      </div>
    );
  };

  const handleFileChange = (event) => {
    if (event.files && event.files[0]) {
      const file = event.files[0];
      setSelectedFile(file);
      const url = URL.createObjectURL(file)
      setProfileImageUrl(url);
    }
  };

  if (userRoleType && !userId) {
    return <Unauthorized />;
  }

  const editProfileFooter = (
    <div>
      <Button label="Close" icon="pi pi-times" severity="danger" outlined onClick={() => setShowEditProfileModal(false)} />
      <Button label="Save Changes" icon="pi pi-check" severity="warning" loading={editProfileLoading} outlined onClick={() => {
        handleSubmit(onSubmit)();
      }} />
    </div>
  );

  const fileHeaderTemplate = (options) => {
    const { className, chooseButton } = options;

    return (
      <div className={className} style={{ backgroundColor: 'transparent', display: 'flex', alignItems: 'center' }}>
        {chooseButton}
      </div>
    );
  };

  const chooseOptions = { icon: 'pi pi-fw pi-images', className: 'custom-choose-btn p-button-rounded p-button-outlined' };
  const resolvedDisplayInitials = displayInitials || deriveInitials(firstName, lastName) || "NA";
  const editableAdditionalRoleOptions = ALL_ADDITIONAL_ROLE_OPTIONS.filter((role) => role.value !== userRoleType);
  const selectedRoleLabels = selectedRoleTypes
    .map((roleType) => ALL_ADDITIONAL_ROLE_OPTIONS.find((role) => role.value === roleType)?.label || authExperience.roleOptions?.find((role) => role.value === roleType)?.label || roleType)
    .filter(Boolean);
  const additionalRoleLabels = selectedRoleLabels.filter((label) => label !== (authExperience.roleOptions?.find((role) => role.value === userRoleType)?.label || userRoleType));

  const sectionCardStyle = {
    border: "1px solid #e3edf5",
    boxShadow: "0 14px 32px rgba(24, 93, 162, 0.08)",
    borderRadius: "16px",
  } as const;

  return (
    <div className="container-fluid p-3">
      <Toast ref={toast} />
      <div style={{ maxWidth: "1080px", margin: "0 auto" }}>
        <div className="mb-4">
          <h2 className="m-0">Profile</h2>
          <div className="mt-2" style={{ color: "#596F80" }}>
            Review your account details and keep your contact information up to date.
          </div>
        </div>
        <Card
          title={titleTemplate("Profile")}
          className="mx-auto"
          style={{ ...sectionCardStyle, maxWidth: "1080px" }}
        >
          <div className="grid align-items-start">
            <div className="col-12 md:col-4 lg:col-3 flex justify-content-center">
              <div className="flex flex-column align-items-center">
                {profileImageUrl ? (
                  <img src={profileImageUrl}
                    width="160"
                    height="160"
                    style={{
                      objectFit: "cover",
                      border: "3px solid #f2c265",
                      borderRadius: "50%",
                      display: "block",
                      boxShadow: "0 10px 24px rgba(24, 93, 162, 0.12)"
                    }}
                  />
                ) : (
                  <div
                    className="flex align-items-center justify-content-center"
                    style={{
                      width: "160px",
                      height: "160px",
                      borderRadius: "50%",
                      background: "linear-gradient(180deg, #fff7ed 0%, #ffffff 100%)",
                      border: "2px solid #fde68a",
                      color: "#9a3412",
                      fontWeight: 700,
                      fontSize: "2rem"
                    }}
                  >
                    {firstName?.charAt(0) || userName?.charAt(0) || "U"}
                  </div>
                )}
                <div className="mt-3 text-center" style={{ color: "#596F80" }}>
                  <div style={{ fontWeight: 700, color: "#1f2937" }}>{firstName} {lastName}</div>
                  <div style={{ textTransform: "capitalize" }}>{userRoleType}</div>
                </div>
                <Button
                  severity="warning"
                  label="Edit Profile"
                  rounded
                  className="p-button-outlined mt-3"
                  loading={loading}
                  onClick={() => {
                    setShowEditProfileModal(true);
                  }}
                />
              </div>
            </div>

            <div className="col-12 md:col-8 lg:col-9">
              <div className="grid">
                <div className="col-12 md:col-6">
                  <div style={{ backgroundColor: "#f8fafc", borderRadius: "12px", padding: "1rem", border: "1px solid #e2e8f0", height: "100%" }}>
                    <div style={{ fontSize: "0.78rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Username</div>
                    <div className="mt-2" style={{ fontWeight: 700, color: "#1f2937" }}>{userName}</div>
                  </div>
                </div>
                <div className="col-12 md:col-6">
                  <div style={{ backgroundColor: "#f8fafc", borderRadius: "12px", padding: "1rem", border: "1px solid #e2e8f0", height: "100%" }}>
                    <div style={{ fontSize: "0.78rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Email</div>
                    <div className="mt-2" style={{ fontWeight: 700, color: "#1f2937", wordBreak: "break-word" }}>{userEmail}</div>
                  </div>
                </div>
                <div className="col-12 md:col-6">
                  <div style={{ backgroundColor: "#f8fafc", borderRadius: "12px", padding: "1rem", border: "1px solid #e2e8f0", height: "100%" }}>
                    <div style={{ fontSize: "0.78rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Phone Number</div>
                    <div className="mt-2" style={{ fontWeight: 700, color: "#1f2937" }}>{phoneNumber || "Not added"}</div>
                  </div>
                </div>
                <div className="col-12 md:col-6">
                  <div style={{ backgroundColor: "#f8fafc", borderRadius: "12px", padding: "1rem", border: "1px solid #e2e8f0", height: "100%" }}>
                    <div style={{ fontSize: "0.78rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Primary Role</div>
                    <div className="mt-2" style={{ fontWeight: 700, color: "#1f2937" }}>
                      {authExperience.roleOptions?.find((role) => role.value === userRoleType)?.label || userRoleType}
                    </div>
                  </div>
                </div>
                <div className="col-12 md:col-6">
                  <div style={{ backgroundColor: "#f8fafc", borderRadius: "12px", padding: "1rem", border: "1px solid #e2e8f0", height: "100%" }}>
                    <div style={{ fontSize: "0.78rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Additional Roles</div>
                    <div className="mt-2" style={{ fontWeight: 700, color: "#1f2937" }}>
                      {additionalRoleLabels.length ? additionalRoleLabels.join(", ") : "None selected"}
                    </div>
                  </div>
                </div>
                <div className="col-12 md:col-6">
                  <div style={{ backgroundColor: "#f8fafc", borderRadius: "12px", padding: "1rem", border: "1px solid #e2e8f0", height: "100%" }}>
                    <div style={{ fontSize: "0.78rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>ProviderOne ID</div>
                    <div className="mt-2" style={{ fontWeight: 700, color: "#1f2937" }}>{providerOneId || "Not added"}</div>
                  </div>
                </div>
                <div className="col-12 md:col-6">
                  <div style={{ backgroundColor: "#f8fafc", borderRadius: "12px", padding: "1rem", border: "1px solid #e2e8f0", height: "100%" }}>
                    <div style={{ fontSize: "0.78rem", color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em" }}>Display Initials</div>
                    <div className="mt-2" style={{ fontWeight: 700, color: "#1f2937" }}>{resolvedDisplayInitials}</div>
                  </div>
                </div>
                <div className="col-12">
                  <div style={{ background: "linear-gradient(180deg, #fffaf0 0%, #ffffff 100%)", borderRadius: "14px", padding: "1rem", border: "1px solid #fde68a" }}>
                    <div style={{ fontSize: "0.78rem", color: "#9a3412", textTransform: "uppercase", letterSpacing: "0.05em" }}>Address</div>
                    <div className="mt-2" style={{ color: "#374151", lineHeight: 1.7 }}>
                      {formattedAddress || "No address saved yet."}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {profileErrorMessage && <div className="p-error mt-3">{profileErrorMessage}</div>}
        </Card>

        <Card
          title={titleTemplate("Communication Preferences")}
          className="mx-auto mt-4"
          style={{ ...sectionCardStyle, maxWidth: "1080px" }}
        >
          <div className="flex flex-column align-items-center text-center">
            <div style={{ color: "#596F80", lineHeight: 1.7, maxWidth: "42rem" }}>
              Manage optional account emails from AFH Hub. This setting only controls Admin campaign emails like product updates, announcements, and platform news.
            </div>

            <div
              className="mt-4"
              style={{
                width: "100%",
                maxWidth: "44rem",
                background: communicationPreferences?.emailCampaignSubscribed
                  ? "linear-gradient(180deg, #fff7ed 0%, #ffffff 100%)"
                  : "#f8fafc",
                border: communicationPreferences?.emailCampaignSubscribed
                  ? "1px solid #fdba74"
                  : "1px solid #e2e8f0",
                borderRadius: "16px",
                padding: "1rem",
              }}
            >
              <div className="flex flex-column align-items-center gap-3">
                <div style={{ maxWidth: "32rem" }}>
                  <div style={{ fontWeight: 800, color: "#0f172a" }}>Admin product updates</div>
                  <div className="mt-2" style={{ color: "#596F80", lineHeight: 1.7 }}>
                    Receive feature announcements, release updates, and important non-billing product emails sent from the Admin.
                  </div>
                  <div className="mt-3" style={{ fontSize: "0.92rem", color: communicationPreferences?.emailCampaignSubscribed ? "#9a3412" : "#475569" }}>
                    Status: {communicationPreferences?.emailCampaignSubscribed ? "Subscribed" : "Unsubscribed"}
                    {communicationPreferences?.updatedAt ? ` • Updated ${new Date(communicationPreferences.updatedAt).toLocaleString()}` : ""}
                  </div>
                </div>

                <div
                  style={{
                    minWidth: "16rem",
                    width: "100%",
                    maxWidth: "20rem",
                    background: "#ffffff",
                    border: "1px solid #e2e8f0",
                    borderRadius: "14px",
                    padding: "0.85rem 1rem",
                  }}
                >
                  <div className="flex flex-column align-items-center text-center gap-3">
                    <div>
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>Receive emails</div>
                      <div className="mt-1" style={{ fontSize: "0.88rem", color: "#64748b" }}>
                        Turn campaign emails on or off anytime.
                      </div>
                    </div>
                    <div className="flex justify-content-center">
                      <Checkbox
                        inputId="emailCampaignSubscribed"
                        checked={communicationPreferences?.emailCampaignSubscribed !== false}
                        disabled={savingCommunicationPreferences || loadingCommunicationPreferences}
                        onChange={(e) => void onCommunicationPreferenceChange(Boolean(e.checked))}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <Dialog header="Edit Profile" visible={showEditProfileModal} modal footer={editProfileFooter} style={{ width: 'min(94vw, 60rem)' }}
        onHide={() => { if (!showEditProfileModal) return; setShowEditProfileModal(false); reset(); resetAddressFields(); }}>
        <Toast ref={toast} />
        <form>
          <div className="grid align-items-start">
            <div className="col-12 md:col-7 p-fluid">
              <div style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "16px", padding: "1.25rem" }}>
                <div className="mb-4">
                  <h4 className="m-0" style={{ color: "#185DA2" }}>Personal Details</h4>
                  <div className="mt-2" style={{ color: "#64748b" }}>
                    Update your name, phone number, and primary address.
                  </div>
                </div>

                <div className="field mb-4">
                  <span className="p-float-label">
                    <Controller name="firstName" control={control} rules={{
                      required: 'Name is required.',
                      maxLength: { value: 100, message: 'Name must be at most 100 characters long.' }
                    }} render={({ field, fieldState }) => (
                      <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                    )} />
                    <label htmlFor="firstName" className={classNames({ 'p-error': errors.firstName })}>First Name</label>
                  </span>
                  {getFormErrorMessage('firstName')}
                </div>

                <div className="field mb-4">
                  <span className="p-float-label">
                    <Controller name="lastName" control={control} rules={{
                      required: 'Name is required.',
                      maxLength: { value: 100, message: 'Name must be at most 100 characters long.' }
                    }} render={({ field, fieldState }) => (
                      <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                    )} />
                    <label htmlFor="lastName" className={classNames({ 'p-error': errors.lastName })}>Last Name</label>
                  </span>
                  {getFormErrorMessage('lastName')}
                </div>

                <div className="field mb-4">
                  <span className="p-float-label">
                    <Controller name="phoneNumber" control={control} rules={{
                      required: 'Phone Number is required.',
                      pattern: { value: /^[0-9()+-\s]*$/, message: 'Phone Number is not valid.' },
                      maxLength: { value: 15, message: 'Phone Number must be at most 15 characters long.' }
                    }} render={({ field, fieldState }) => (
                      <InputText type="tel" id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                    )} />
                    <label htmlFor="phoneNumber" className={classNames({ 'p-error': errors.phoneNumber })}>Phone Number</label>
                  </span>
                  {getFormErrorMessage('phoneNumber')}
                </div>

                {userRoleType === ROLE_TYPE_PROVIDER ? (
                  <div className="field mb-4">
                    <span className="p-float-label">
                      <Controller name="providerOneId" control={control} rules={{
                        maxLength: { value: 40, message: 'ProviderOne ID must be at most 40 characters long.' }
                      }} render={({ field, fieldState }) => (
                        <InputText id={field.name} {...field} className={classNames({ 'p-invalid': fieldState.invalid })} />
                      )} />
                      <label htmlFor="providerOneId" className={classNames({ 'p-error': errors.providerOneId })}>ProviderOne ID</label>
                    </span>
                    {getFormErrorMessage('providerOneId')}
                  </div>
                ) : null}

                <div className="field mb-4">
                  <span className="p-float-label">
                    <Controller name="displayInitials" control={control} rules={{
                      maxLength: { value: 6, message: 'Display Initials must be at most 6 characters long.' }
                    }} render={({ field, fieldState }) => (
                      <InputText
                        id={field.name}
                        {...field}
                        onChange={(event) => field.onChange(String(event.target.value || "").toUpperCase())}
                        className={classNames({ 'p-invalid': fieldState.invalid })}
                      />
                    )} />
                    <label htmlFor="displayInitials" className={classNames({ 'p-error': errors.displayInitials })}>Display Initials</label>
                  </span>
                  <small style={{ color: "#64748b", display: "block", marginTop: "0.35rem" }}>
                    Used for staff initials and other compact labels. Leave blank to auto-use initials from your first and last name.
                  </small>
                  {getFormErrorMessage('displayInitials')}
                </div>

                <div className="field mb-4">
                  <label htmlFor="additionalRoleTypes">Additional Roles</label>
                  <Controller
                    name="additionalRoleTypes"
                    control={control}
                    render={({ field, fieldState }) => (
                      <MultiSelect
                        id={field.name}
                        value={Array.isArray(field.value) ? field.value : []}
                        options={editableAdditionalRoleOptions}
                        optionLabel="label"
                        optionValue="value"
                        disabled={!canEditAdditionalRoles}
                        onChange={(event) => field.onChange(Array.isArray(event.value) ? event.value : [])}
                        placeholder="Select any additional roles"
                        display="chip"
                        className={classNames({ 'p-invalid': fieldState.invalid })}
                        style={{ width: "100%" }}
                      />
                    )}
                  />
                  <small style={{ color: "#64748b", display: "block", marginTop: "0.35rem" }}>
                    {canEditAdditionalRoles
                      ? `Your primary role stays ${authExperience.roleOptions?.find((role) => role.value === userRoleType)?.label || userRoleType}. Add any other roles you also cover.`
                      : "Additional roles can only be edited by Provider users."}
                  </small>
                </div>

                <div className="field mb-4">
                  <label>Address</label>
                  <span className="p-float-label">
                    <Controller name="address" control={control}
                      rules={{ required: 'Please select your full address from the Google suggestions.' }}
                      render={({ field, fieldState }) => (
                        <SearchAddress id={field.name} {...field}
                          onChange={handleAddressChange}
                          className={classNames({ 'p-invalid': fieldState.invalid })} />
                      )} />
                  </span>
                  {getFormErrorMessage('address')}
                </div>
              </div>
            </div>
            <div className="col-12 md:col-5">
              <div style={{ background: "linear-gradient(180deg, #fffaf0 0%, #ffffff 100%)", border: "1px solid #fde68a", borderRadius: "16px", padding: "1.5rem", height: "100%" }}
                className="flex flex-column align-items-center justify-content-center">
                <h4 className="mb-2 mt-0" style={{ color: "#9a3412" }}>Profile Image</h4>
                <div className="mb-3 text-center" style={{ color: "#64748b" }}>
                  Add or replace your profile image.
                </div>

                {profileImageUrl ? (
                  <img className="mb-3"
                    src={profileImageUrl}
                    width="110"
                    height="110"
                    style={{
                      objectFit: "cover",
                      border: "3px solid #f2c265",
                      borderRadius: "50%",
                      boxShadow: "0 10px 24px rgba(24, 93, 162, 0.12)"
                    }}
                  />) : (
                  <div
                    className="mb-3 flex align-items-center justify-content-center"
                    style={{
                      width: "110px",
                      height: "110px",
                      borderRadius: "50%",
                      backgroundColor: "#fff",
                      border: "2px dashed #f2c265",
                      color: "#9a3412",
                      fontWeight: 700,
                      fontSize: "1.5rem"
                    }}
                  >
                    {firstName?.charAt(0) || userName?.charAt(0) || "U"}
                  </div>
                )}

                <FileUpload mode="basic" name="profileImage" onSelect={handleFileChange} accept="image/*"
                  maxFileSize={WEBSITE_TEMPLATE_MAX_IMAGE_FILE_SIZE_IN_BYTES}
                  headerTemplate={fileHeaderTemplate} chooseOptions={chooseOptions}
                  emptyTemplate={<p className="m-0">Drag and drop image here to upload.</p>} />
              </div>
            </div>
          </div>
          <Divider />
          {editProfileErrorMessage && <div className="p-error m-3">{editProfileErrorMessage}</div>}
        </form>
      </Dialog>
    </div >
  );
}

export default Profile;

